import { useTranslation } from '@/lib/i18n';
import { Button, PageShell, Pagination, Skeleton } from '@/components/ui';
import { useVendorFiltersStore, type VendorSort } from '@/stores/useVendorFiltersStore';
import { VendorFiltersBar } from '../components/VendorFiltersBar';
import { VendorApplicationsTable } from '../components/VendorApplicationsTable';
import { useVendorApplicationsQuery } from '../hooks/useVendorApplicationsQuery';

const VENDOR_SORT_VALUES = new Set<VendorSort>([
  'createdAt,desc',
  'createdAt,asc',
  'submittedAt,desc',
  'submittedAt,asc',
  'slaDueAt,asc',
  'slaDueAt,desc',
]);

const isVendorSort = (s: string): s is VendorSort =>
  VENDOR_SORT_VALUES.has(s as VendorSort);

export const VendorApplicationListPage = () => {
  const { t } = useTranslation('vendors');

  const keyword = useVendorFiltersStore((s) => s.keyword);
  const status = useVendorFiltersStore((s) => s.status);
  const slaDueBefore = useVendorFiltersStore((s) => s.slaDueBefore);
  const assignedReviewerId = useVendorFiltersStore((s) => s.assignedReviewerId);
  const page = useVendorFiltersStore((s) => s.page);
  const size = useVendorFiltersStore((s) => s.size);
  const sort = useVendorFiltersStore((s) => s.sort);
  const setSort = useVendorFiltersStore((s) => s.setSort);
  const setPage = useVendorFiltersStore((s) => s.setPage);

  const query = useVendorApplicationsQuery({
    page,
    size,
    sort,
    keyword,
    status,
    slaDueBefore,
    assignedReviewerId,
  });

  const totalItems = query.data?.meta.pagination?.totalItems ?? 0;
  const items = query.data?.items ?? [];

  return (
    <PageShell title={t('vendors.list.title')}>
      <VendorFiltersBar />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
          }}
        >
          <p>{t('vendors.list.error')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('vendors.actions.retry')}
          </Button>
        </div>
      ) : (
        <VendorApplicationsTable
          items={items}
          sort={sort}
          onSortChange={(next) => {
            if (isVendorSort(next)) setSort(next);
          }}
        />
      )}

      <Pagination
        total={totalItems}
        currentPage={page}
        pageSize={size}
        onPageChange={setPage}
        aria-label={t('vendors.list.paginationLabel')}
      />
    </PageShell>
  );
};
